import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { getContrastColor } from '../../utils/utils';
import './GridTile.styl';

/**
 * @typedef {Object} LiveGalleryGridImage
 * @property {string} id - Unique identifier for the image.
 * @property {"image"|"video"} renderType - The media type to render as (image or video).
 * @property {string} src - The source URL of the image or video.
 * @property {string} [static] - Static image URL for video.
 */

/**
 * @typedef {Object} GridTileProps
 * @property {LiveGalleryGridImage} image - The image or video to display.
 * @property {string} [eventTitle] - Title of the event associated with the image.
 * @property {string} [eventDate] - Date of the event, formatted as an ISO string.
 * @property {string} [accentColor] - Accent color for UI elements.
 * @property {string} [href] - Optional link to navigate to when the tile is clicked.
 * @property {boolean} [newTab=false] - Whether to open the link in a new tab.
 * @property {Function} [onClick] - Optional click handler for the tile. *
 */
const GridTile = ({ image, eventTitle, eventDate, accentColor, href, newTab = false, onClick }) => {
  const handleClick = useCallback(() => {
    if (!onClick || !image?.id) {
      return;
    }
    onClick(image.id);
  }, [onClick, image]);

  const formattedEventDate = useMemo(() => (eventDate ? moment(eventDate).format('M/D/YYYY') : ''), [eventDate]);
  const contrastColor = useMemo(() => getContrastColor(accentColor), [accentColor]);

  const renderType = image.renderType ?? null;
  const TileTag = href ? 'a' : 'button';

  const HoverOverlay = () => {
    if (eventTitle && formattedEventDate) {
      return (
        <div className="imagetile__overlay">
          <div className="imagetile__content">
            <h2 className="imagetile__title">{eventTitle}</h2>
            <div className="imagetile__date">{formattedEventDate}</div>
            <div
              className="btn btn--secondary"
              style={{
                backgroundColor: accentColor,
                color: contrastColor,
              }}
            >
              View Gallery
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // If no image source, don't render anything
  if (!image?.src) {
    return null;
  }

  switch (renderType) {
    case 'image':
      return (
        <TileTag
          className={'imagetile imagetile--image'}
          onClick={handleClick}
          href={href}
          target={newTab ? '_blank' : null}
        >
          <img src={image.src} alt="" />
          <HoverOverlay />
        </TileTag>
      );
    case 'video':
      return (
        <TileTag
          className={'imagetile imagetile--video'}
          onClick={handleClick}
          href={href}
          target={newTab ? '_blank' : null}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            disableRemotePlayback={true}
            preload="none"
            src={image.src}
            type="video/mp4"
            style={{
              backgroundImage: `url(${image.static})`,
            }}
          ></video>
          <HoverOverlay />
        </TileTag>
      );
    default:
      return (
        <TileTag
          className={'imagetile imagetile--image'}
          onClick={handleClick}
          href={href}
          target={newTab ? '_blank' : null}
        >
          <div className="imagetile__noimage">No image</div>
          <HoverOverlay />
        </TileTag>
      );
  }
};

export default GridTile;
